import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password, remember);
      nav(u.role === "tenant" ? "/tenant" : "/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <SEO title="Sign In" description="Sign in to your RealtyVault Manager owner console." path="/login" noindex />
      <div className="hidden lg:flex bg-marketing grid-texture text-white p-12 flex-col justify-between relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2 text-white" data-testid="login-back-home">
          <ArrowLeft className="w-4 h-4" /> <span className="font-semibold">Back to site</span>
        </Link>
        <div className="relative z-10">
          <div className="overline text-white/40 mb-4">Owner Console</div>
          <h2 className="text-4xl font-bold tracking-tighter max-w-md" style={{fontFamily:"Outfit"}}>
            Your commercial portfolio, <span className="italic font-light text-white/50">one screen.</span>
          </h2>
          <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
            <div className="glass rounded-lg p-4"><div className="text-xs uppercase tracking-widest text-white/40">Rent Collected</div><div className="text-2xl font-mono mt-1">$428K</div></div>
            <div className="glass rounded-lg p-4"><div className="text-xs uppercase tracking-widest text-white/40">Properties</div><div className="text-2xl font-mono mt-1">42</div></div>
          </div>
        </div>
        <div className="text-xs text-white/30">© 2026 RealtyVault Manager</div>
      </div>

      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          <div className="overline text-slate-500 mb-3">Sign in</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"Outfit"}}>Welcome back.</h1>
          <p className="mt-2 text-sm text-slate-500">Access your owner console.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <label className="overline text-slate-600">Email</label>
              <input
                type="email"
                data-testid="login-email-input"
                className="w-full mt-2 input-clean"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="overline text-slate-600">Password</label>
                <Link to="/forgot-password" className="text-xs text-slate-500 hover:text-slate-900 hover:underline" data-testid="login-forgot-password">
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                data-testid="login-password-input"
                className="w-full mt-2 input-clean"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>
            {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="login-remember-label">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-4 h-4 rounded-sm border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer"
                data-testid="login-remember-checkbox"
              />
              Keep me signed in for 30 days
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full btn-primary-dark flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>
          <div className="mt-6 text-sm text-slate-500 space-y-2">
            <div>No account? <Link to="/signup" className="text-slate-900 font-semibold hover:underline" data-testid="login-go-signup">Create one</Link></div>
            <div>Tenant? <Link to="/tenant-login" className="text-slate-900 font-semibold hover:underline" data-testid="login-go-tenant">Sign in here</Link></div>
            <div className="text-xs text-slate-400">Your username is the email address on your account.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
